import clsx from "clsx";
import { useSpotlitControl } from "@/contexts/WidgetSpotlightContext";
import { HighlighterIcon } from "./HighlighterIcon";

export interface HighlightPanelToggleProps {
  /** Whether the panel is currently open */
  isOpen: boolean;
  /** Whether there are any active highlight selections */
  hasSelections: boolean;
  /** Toggle callback */
  onToggle: () => void;
  /** Optional tooltip/title */
  title?: string;
}

export default function HighlightPanelToggle({
  isOpen,
  hasSelections,
  onToggle,
  title = "Highlight options",
}: HighlightPanelToggleProps) {
  // Null everywhere except inside the widget the guided demo is pointing at,
  // and null always in popouts — the provider is main-window only.
  const glow = useSpotlitControl() === "highlight" && !isOpen;

  return (
    <button
      onClick={onToggle}
      // Opens the menu the guided demo's highlight step is asking for.
      data-analytics-id="widget-highlight-toggle"
      className={clsx(
        glow && "nq-demo-pulse-warm",
        "p-1 no-drag transition-all duration-200",
        // Menu open = solid fill
        isOpen && "bg-terminal-orange",
        // Active + closed = subtle indicator
        hasSelections && !isOpen && "bg-transparent",
        // Default = neutral
        !hasSelections &&
          !isOpen &&
          "bg-transparent hover:bg-gray-200 dark:hover:bg-gray-600"
      )}
      title={isOpen ? "Close highlights" : title}
    >
      <HighlighterIcon
        filled={hasSelections && !isOpen}
        className={clsx("size-4", {
          "text-white": isOpen || glow,
          "text-terminal-orange": hasSelections && !isOpen,
          "text-gray-500 dark:text-gray-300": !hasSelections && !isOpen,
        })}
      />
    </button>
  );
}
